import { Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import * as moment from 'moment';
import { BaseComponent } from '../../../base/base.component';
import { AthleteSeason } from '../../../../models/athleteSeason';
import { CompanyDocument } from '../../../../models/CompanyDocument';
import { AthleteService } from '../../../../services/athlete/athlete.service';
import { CategoryService } from '../../../../services/category/category.service';
import { CompanyService } from '../../../../services/company/company.service';
import { ConvocationService } from '../../../../services/convocation/convocation.service';
import { environment } from '../../../../environments/environment';
import {
    type ConvocatedUserPreview,
    type ConvocationRowItem,
    convocationCityLine,
    convocationDirectionsUrl,
    isConvocationLink,
    relativeConvocationDay,
} from '../mister-convocation-shared';

@Component({
    selector: 'app-mister-convocation-detail',
    templateUrl: './mister-convocation-detail.component.html',
    styleUrls: ['./mister-convocation-detail.component.scss'],
})
export class DashboardMisterConvocationDetailComponent
    extends BaseComponent
    implements OnInit, OnDestroy
{
    convocation?: ConvocationRowItem;
    invitedUsers: ConvocatedUserPreview[] = [];
    categoryAthleteSeasons: AthleteSeason[] = [];
    primaryDocumentExists = false;
    detailLoading = false;
    readonly now = moment();

    private readonly companyId = localStorage.getItem('wisper_companyId') ?? '';
    private currentAthleteId?: string;

    /** Skeleton rows in “Chi è convocato” block while detail loads */
    readonly invitedRowSkeletonPlaceholders = [0, 1, 2, 3, 4];
    formOpen = false;
    readOnly = false;
    backRoute: string[] = ['/misters/dashboard'];
    backQueryParams?: Record<string, string>;

    protected sub = new Subscription();

    constructor(
        injector: Injector,
        private readonly route: ActivatedRoute,
        private convocationService: ConvocationService,
        private categoryService: CategoryService,
        private athleteService: AthleteService,
        private companyService: CompanyService,
    ) {
        super(injector);
    }

    get isMisterView(): boolean {
        return !this.readOnly;
    }

    get invitedAthleteSeasons(): AthleteSeason[] {
        const invitedIds = new Set(this.invitedUsers.map((u) => u.id));
        return this.categoryAthleteSeasons.filter((row) =>
            invitedIds.has(row.athlete?.id ?? ''),
        );
    }

    get notInvitedAthleteSeasons(): AthleteSeason[] {
        const invitedIds = new Set(this.invitedUsers.map((u) => u.id));
        return this.categoryAthleteSeasons.filter(
            (row) => !invitedIds.has(row.athlete?.id ?? ''),
        );
    }

    get athleteViewInvitedUsers(): ConvocatedUserPreview[] {
        if (this.isMisterView) {
            return this.invitedUsers;
        }
        if (!this.currentAthleteId) {
            return this.invitedUsers;
        }
        return this.invitedUsers.filter((u) => u.id !== this.currentAthleteId);
    }

    ngOnInit(): void {
        // Athletes and parents ("user" group) may reach the mister URL too: their view never
        // depends on the route, so teammates' contacts and fiscal codes are not loaded for them.
        const athleteUser = this.getUserType() === 'user';
        this.readOnly = athleteUser || !!this.route.snapshot.data['readOnly'];
        const back = this.route.snapshot.data['backRoute'];
        if (Array.isArray(back) && back.length > 0) {
            this.backRoute = back;
        } else if (athleteUser) {
            this.backRoute = ['/users/convocations'];
        }
        // Company area: `/companies/subscriptions/category/:categoryId/convocations/:id`.
        const categoryId = this.route.snapshot.paramMap.get('categoryId');
        if (categoryId && !athleteUser) {
            this.backRoute = ['/companies/subscriptions/category', categoryId];
            this.backQueryParams = {tab: 'convocations'};
        }
        this.sub.add(
            this.route.paramMap.subscribe((pm) => {
                void this.loadConvocation(pm.get('id'));
            }),
        );
    }

    ngOnDestroy(): void {
        this.sub.unsubscribe();
    }

    private async loadConvocation(id: string | null): Promise<void> {
        this.invitedUsers = [];
        this.categoryAthleteSeasons = [];
        this.currentAthleteId = undefined;
        this.convocation = undefined;
        if (!id) {
            return;
        }
        this.detailLoading = true;
        try {
            const res = await this.convocationService.getConvocationById(id);
            if (res?.success && res.data) {
                let row = this.convocationService.mapApiRecordToRow(res.data);
                if (row.categoryId && !row.categoryName) {
                    const cat = await this.categoryService.getCategoryById(
                        row.categoryId,
                    );
                    if (cat?.name) {
                        row = { ...row, categoryName: cat.name };
                    }
                }
                this.convocation = row;
                await this.loadInvitedPreviews(row.users ?? []);
                if (this.isMisterView) {
                    await Promise.all([
                        this.loadCategoryAthleteSeasons(row.categoryId),
                        this.loadPrimaryDocumentCheck(),
                    ]);
                } else {
                    await this.loadCurrentAthleteId();
                }
            } else {
                this.convocation = undefined;
            }
        } finally {
            this.detailLoading = false;
        }
    }

    private async loadInvitedPreviews(ids: string[]): Promise<void> {
        const out: ConvocatedUserPreview[] = [];
        for (const id of ids) {
            if (!id) {
                continue;
            }
            try {
                const a = await this.athleteService.getAthleteById(id);
                if (a?.id) {
                    out.push({
                        id: a.id,
                        fullName:
                            `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim() ||
                            '—',
                    });
                }
            } catch {
                /* skip */
            }
        }
        this.invitedUsers = out.sort((x, y) =>
            x.fullName.localeCompare(y.fullName, 'it'),
        );
    }

    private async loadCategoryAthleteSeasons(
        categoryId: string | undefined,
    ): Promise<void> {
        const cid = (categoryId ?? '').trim();
        if (!cid) {
            this.categoryAthleteSeasons = [];
            return;
        }
        try {
            const response: any = await this.athleteService.http
                .get(`${environment.apiUrl}/athletesSeasons`, {
                    params: { categoryId: cid, $skip: 0, $top: 500 },
                    headers: this.athleteService.getRequestHeaders(),
                })
                .toPromise();
            this.categoryAthleteSeasons = response?.success
                ? response.data.map(
                      (item: AthleteSeason) => new AthleteSeason(item),
                  )
                : [];
        } catch {
            this.categoryAthleteSeasons = [];
        }
    }

    private async loadPrimaryDocumentCheck(): Promise<void> {
        this.primaryDocumentExists = false;
        if (!this.companyId) {
            return;
        }
        try {
            const documents = await this.companyService.getCompanyDocuments(
                this.companyId,
            );
            this.primaryDocumentExists = documents.some(
                (document: CompanyDocument) => document.primaryDocument === 1,
            );
        } catch {
            this.primaryDocumentExists = false;
        }
    }

    private async loadCurrentAthleteId(): Promise<void> {
        const userId = localStorage.getItem('wisper_userId') ?? '';
        if (!userId) {
            return;
        }
        try {
            const response: any = await this.athleteService.getAllAthlete({
                userId,
            });
            this.currentAthleteId = response?.data?.[0]?.id as
                | string
                | undefined;
        } catch {
            this.currentAthleteId = undefined;
        }
    }

    isMembershipActive(row: AthleteSeason): boolean {
        const memberships = row.athlete?.memberships ?? [];
        if (!memberships.length) {
            return false;
        }
        return moment(
            memberships[0].badgeExpiration,
            'YYYY-MM-DD HH:mm:ss',
        ).isAfter(this.now);
    }

    membershipLabel(row: AthleteSeason): string {
        return this.isMembershipActive(row) ? 'Tesserato' : 'Non tesserato';
    }

    membershipExpiryLabel(row: AthleteSeason): string | null {
        if (!this.isMembershipActive(row)) {
            return null;
        }
        const exp = row.athlete!.memberships[0].badgeExpiration;
        return moment(exp, 'YYYY-MM-DD HH:mm:ss').format('DD-MM-YYYY');
    }

    statusClass(status: string): string {
        const key = status.toLowerCase();
        if (
            key.includes('pubblicat') ||
            key.includes('open') ||
            key.includes('attiv') ||
            key === 'active'
        ) {
            return '!bg-emerald-50 !text-emerald-800 !ring-emerald-600/15';
        }
        if (key.includes('bozz') || key.includes('draft')) {
            return '!bg-amber-50 !text-amber-800 !ring-amber-600/15';
        }
        if (
            key.includes('chius') ||
            key.includes('annull') ||
            key.includes('cancell')
        ) {
            return '!bg-zinc-100 !text-zinc-700 !ring-zinc-500/10';
        }
        return '!bg-sky-50 !text-sky-800 !ring-sky-600/15';
    }

    isLink(value: string): boolean {
        return isConvocationLink(value);
    }

    cityLine(c: ConvocationRowItem): string {
        return convocationCityLine(c);
    }

    directionsUrl(c: ConvocationRowItem): string | null {
        return convocationDirectionsUrl(c);
    }

    relativeDay(date: string): { label: string; past: boolean } | null {
        return relativeConvocationDay(date);
    }

    /** Invited athletes whose membership is not active: the coach should sort these out before the event. */
    get invitedWithoutMembership(): number {
        return this.invitedAthleteSeasons.filter((row) => !this.isMembershipActive(row)).length;
    }

    trackUserId(_i: number, u: ConvocatedUserPreview): string {
        return u.id;
    }

    openEditDrawer(): void {
        this.formOpen = true;
    }

    async onFormSaved(_updated: ConvocationRowItem): Promise<void> {
        this.formOpen = false;
        await this.loadConvocation(this.route.snapshot.paramMap.get('id'));
    }

    navigateBackToDashboard(): void {
        void this.navigate.navigate(this.backRoute, {queryParams: this.backQueryParams});
    }
}
